import { useEffect, useState } from "react"
import styled, { keyframes } from "styled-components"

// Extend the Window interface to include gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: Array<Record<string, string | number | boolean | undefined>>
  }
}

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const PopupContainer = styled.div`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  width: min(560px, calc(100% - 2rem));
  z-index: 999;
  animation: ${slideUp} 0.4s ease forwards;
  pointer-events: none;

  @media (max-width: 640px) {
    right: 0.75rem;
    bottom: 0.75rem;
    width: calc(100% - 1.5rem);
  }
`

const Popup = styled.div`
  background: rgba(12, 28, 48, 0.92);
  backdrop-filter: blur(12px);
  border-radius: 2px;
  padding: 1.2rem 1.4rem;
  display: grid;
  gap: 1rem;
  box-shadow: 0 18px 40px rgba(8, 21, 38, 0.28);
  color: #ffffff;
  border: 1px solid rgba(99, 195, 255, 0.2);
  pointer-events: auto;

  @media (min-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: #ffffff;
`

const Description = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.86);
`

const LearnMoreLink = styled.a`
  color: #63c3ff;
  font-weight: 600;
  text-decoration: none;

  &:hover,
  &:focus {
    color: #ffffff;
    text-decoration: underline;
  }
`

const Message = styled.div`
  display: grid;
  gap: 0.4rem;
`

const Preferences = styled.div`
  display: grid;
  gap: 0.8rem;
  grid-column: 1 / -1;
`

const PreferenceToggle = styled.label<{ disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
`

const ToggleText = styled.div`
  display: grid;
  gap: 0.15rem;

  span:first-child {
    font-weight: 600;
    font-size: 0.95rem;
  }

  span:last-child {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
  }
`

const Switch = styled.input`
  appearance: none;
  width: 46px;
  height: 24px;
  border-radius: 999px;
  position: relative;
  background: rgba(255, 255, 255, 0.2);
  transition: background 0.2s ease;
  cursor: pointer;

  &:checked {
    background: linear-gradient(135deg, #63c3ff 0%, #1b75bc 100%);
  }

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.2s ease;
  }

  &:checked::after {
    transform: translateX(22px);
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  justify-content: flex-end;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`

const PreferencesActions = styled(Actions)`
  grid-column: 1 / -1;
`

const Button = styled.button<{ $variant?: "primary" | "ghost" }>`
  border: none;
  border-radius: 2px;
  padding: 0.85rem 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  ${({ $variant = "primary" }) =>
    $variant === "primary"
      ? `
    background: linear-gradient(135deg, #63c3ff 0%, #1b75bc 100%);
    color: #0a233d;
    box-shadow: 0 18px 45px rgba(99, 195, 255, 0.35);
  `
      : `
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.35);
    color: #ffffff;
  `}

  &:hover {
    transform: translateY(-2px);
  }
`

const CookiePopup = () => {
  const [showPopup, setShowPopup] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)
  const [adsEnabled, setAdsEnabled] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)

  function updateConsent(analytics: boolean, ads: boolean) {
    const analyticsStatus = analytics ? "granted" : "denied"
    const adStatus = ads ? "granted" : "denied"

    window.gtag?.("consent", "update", {
      analytics_storage: analyticsStatus,
      ad_storage: adStatus,
      ad_user_data: adStatus,
      ad_personalization: adStatus,
    })
  }

  useEffect(() => {
    const savedConsent = document.cookie
      .split("; ")
      .find((row) => row.startsWith("cookie_consent="))
      ?.split("=")[1]

    if (savedConsent) {
      const preferences = savedConsent.split(",")
      const analyticsAllowed = preferences.includes("analytics")
      const adsAllowed = preferences.includes("ads")
      setAnalyticsEnabled(analyticsAllowed)
      setAdsEnabled(adsAllowed)
      updateConsent(analyticsAllowed, adsAllowed)
    } else {
      setShowPopup(true)
    }
  }, [])

  const persistConsent = (allowAnalytics: boolean, allowAds: boolean) => {
    const preferences = ["essential"]
    if (allowAnalytics) {
      preferences.push("analytics")
    }
    if (allowAds) {
      preferences.push("ads")
    }

    const value = preferences.join(",")
    document.cookie = `cookie_consent=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`
  }

  const handleAcceptAll = () => {
    setAnalyticsEnabled(true)
    setAdsEnabled(true)
    persistConsent(true, true)
    updateConsent(true, true)
    setShowPopup(false)
  }

  const handleRejectAll = () => {
    setAnalyticsEnabled(false)
    setAdsEnabled(false)
    persistConsent(false, false)
    updateConsent(false, false)
    setShowPopup(false)
  }

  const handleSavePreferences = () => {
    persistConsent(analyticsEnabled, adsEnabled)
    updateConsent(analyticsEnabled, adsEnabled)
    setShowPopup(false)
  }

  if (!showPopup) return null

  return (
    <PopupContainer data-nosnippet>
      <Popup role="dialog" aria-live="polite" aria-label="Préférences de témoins">
        <Message>
          <Title>Témoins et confidentialité</Title>
          <Description>
            Les témoins essentiels font fonctionner le site. Avec votre accord, nous pouvons aussi mesurer l’audience et activer la publicité.{" "}
            <LearnMoreLink href="/politique-de-confidentialite">Politique de confidentialité</LearnMoreLink>
          </Description>
        </Message>
        {showPreferences ? (
          <>
            <Preferences id="cookie-preferences" aria-live="polite">
              <PreferenceToggle disabled>
                <ToggleText>
                  <span>Essentiels</span>
                  <span>Indispensables pour la sécurité et le fonctionnement du site.</span>
                </ToggleText>
                <Switch type="checkbox" checked disabled aria-label="Cookies essentiels activés" />
              </PreferenceToggle>
              <PreferenceToggle>
                <ToggleText>
                  <span>Mesure d'audience</span>
                  <span>Autoriser Google Analytics (données anonymisées).</span>
                </ToggleText>
                <Switch
                  type="checkbox"
                  checked={analyticsEnabled}
                  onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                  aria-label="Activer Google Analytics"
                />
              </PreferenceToggle>
              <PreferenceToggle>
                <ToggleText>
                  <span>Publicité</span>
                  <span>Autoriser la personnalisation publicitaire (Google Ads).</span>
                </ToggleText>
                <Switch
                  type="checkbox"
                  checked={adsEnabled}
                  onChange={(event) => setAdsEnabled(event.target.checked)}
                  aria-label="Activer la publicité personnalisée"
                />
              </PreferenceToggle>
            </Preferences>
            <PreferencesActions>
              <Button $variant="ghost" type="button" onClick={handleRejectAll}>
                Tout refuser
              </Button>
              <Button $variant="ghost" type="button" onClick={handleSavePreferences}>
                Enregistrer mes choix
              </Button>
              <Button type="button" onClick={handleAcceptAll}>
                Tout accepter
              </Button>
            </PreferencesActions>
          </>
        ) : (
          <Actions>
            <Button $variant="ghost" type="button" onClick={handleRejectAll}>
              Tout refuser
            </Button>
            <Button $variant="ghost" type="button" onClick={() => setShowPreferences(true)}>
              Paramètres
            </Button>
            <Button type="button" onClick={handleAcceptAll}>
              Tout accepter
            </Button>
          </Actions>
        )}
      </Popup>
    </PopupContainer>
  )
}

export default CookiePopup
